import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import { bindActionCreators } from 'redux';
import styled from 'styled-components';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { Alert, Checkbox, IconButton, Snackbar, TablePagination, Tooltip } from '@mui/material';
import ConfirmPopupForm from '../../components/common/ConfirmPopupForm';
import { filterLibraryService } from '../../services';
import { loaderAction } from '../../store/loaderStore';
import AddFilter from './AddFilter';
import EditFilter from './EditFilter';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { AppContainer, BodyContainer, MainContentContainer } from '../../resources/styling/appStyle';
import {
  sa,
  DataTable,
  Card,
  PillButton,
  SectionHeaderRow,
} from '../../components/superAdminUi';

// The Super Admin Filter Library on the redesigned surface: a section header with the Unapproved
// toggle and Add action, a search card, and the shared purple-header zebra DataTable with
// server-side sort and pagination. Presentation only — every service call, search, filter, sort,
// pagination and CRUD action (add / edit / approve / delete) is preserved.

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sa.space.section};
  width: 100%;
  font-family: ${sa.font.family};
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;  
  justify-content: flex-end;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;  
  width: 500px;
  background: ${sa.color.white};
  border: 1px solid ${sa.color.stroke};
  border-radius: ${sa.radius.sm};
  padding: 8px 12px;

  svg {
    color: ${sa.color.textGray};
    font-size: 20px;
  }
  input {
    flex: 1;
    border: none;
    outline: none;
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    background: transparent;
  }
`;

const FilterField = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${sa.font.family};
  font-size: 13px;
  font-weight: ${sa.font.semibold};
  color: ${sa.color.textGray};
`;

// Clickable purple-header cell that toggles the same server-side sort as before; the arrow
// reflects the active column and direction.
const HeaderSort = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
`;

const SortArrow = styled.span`
  font-size: 11px;
  line-height: 1;
`;

// Table cells ellipsis at the column width, so a long job title reads as "Sports Department
// Assis…" with no way to see the rest. Carrying the full value in `title` gives it back on hover
// without widening the column.
const TruncatedText = ({ value }) =>
  value ? <span title={value}>{value}</span> : <>&mdash;</>;

TruncatedText.propTypes = { value: PropTypes.string };

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: ${sa.radius.chip};
  font-family: ${sa.font.family};
  font-weight: ${sa.font.semibold};
  font-size: 12px;
  line-height: 1;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
`;

const HodCell = styled.span`
  display: inline-flex;
  align-items: center;
  color: ${(p) => (p.$on ? sa.color.teal : sa.color.textGray)};
`;

const ActionCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
`;

const PaginationWrap = styled.div`
  .MuiTablePagination-root {
    font-family: ${sa.font.family};
    color: ${sa.color.textGray};
  }
`;

const APPROVED_TONE = { bg: '#dff5ea', fg: '#1f9d6b' };
const UNAPPROVED_TONE = { bg: sa.color.groupBg, fg: sa.color.pink };

const FilterLibrary = (props) => {
  const getFilterLibrary = () => {
    if (pageState !== undefined) {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        data: old.data,
        total: old.total,
        queryOptions: old.queryOptions,
        filterOptions: old.filterOptions,
        value: old.value,
      }));
    }

    let filter = null;
    if (pageState?.filterOptions?.field !== '') {
      filter = [
        {
          Field: pageState.filterOptions.field,
          Value: pageState.filterOptions.value,
        },
      ];
    }

    let order = null;
    if (pageState?.queryOptions?.field !== '') {
      order = [
        {
          Field: pageState.queryOptions.field,
          Sort: pageState.queryOptions.sort,
        },
      ];
    }
    const model = {
      Start: (pageState.page - 1) * pageState.pageSize,
      Length: pageState.pageSize,
      Order: order,
      Filter: filter,
      Search: {
        Value: pageState.value,
      },
      Columns: [],
    };

    filterLibraryService
      .getFilterLibrary(model)
      .then((response) => {
        const { data, success } = response;
        if (success) {
          const dataRow = [...data.Data].map((item, index) => {
            return {
              id: item.Id,
              keyWord: item.JobTitle,
              jobRole: item.JobRole,
              department: item.Department,
              phaseYear: item.PhaseYear,
              school: item?.School?.Name,
              isApproved: item.IsApproved,
              isHOD: item.IsHOD,
            };
          });
          setPageState((old) => ({
            ...old,
            isLoading: false,
            data: dataRow,
            total: data.RecordsFiltered,
          }));
        } else {
          setPageState((old) => ({
            ...old,
            isLoading: false,
          }));
        }
      })
      .catch((error) => {
        setPageState((old) => ({
          ...old,
          isLoading: false,
          data: old.data,
          total: old.total,
        }));
        // loaderActions.dataLoading(false);
      });
  };

  const [filterId, setFilterId] = useState(0);
  const [isShowEditFilter, setIsShowEditFilter] = useState(false);
  const [isShowAddFilter, setIsShowAddFilter] = useState(false);
  const [isRemovePopupOpen, setIsRemovePopupOpen] = useState(false);
  const [isApprovePopupOpen, setIsApprovePopupOpen] = useState(false);
  const [filterModel, setFilterModel] = useState(null);
  const [isUnApproved, setIsUnApproved] = useState(false);

  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 10,
    queryOptions: { field: '', sort: '' },
    value: null,
    filterOptions: { field: '', value: 0 },
  });
  const [snackbar, setSnackbar] = React.useState(null);

  useEffect(() => {
    getFilterLibrary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageState.page,
    pageState.pageSize,
    pageState.queryOptions,
    pageState.value,
    pageState.filterOptions,
  ]);

  const handleClose = () => {
    setIsShowAddFilter(false);
    setIsShowEditFilter(false);
    setIsApprovePopupOpen(false);
  };
  const handleCloseSnackbar = () => setSnackbar(null);

  const handleSortModelChange = React.useCallback((sortModel) => {
    if (sortModel?.length > 0) {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        total: 0,
        page: 1,
        pageSize: old.pageSize,
        queryOptions: {
          field: sortModel[0].field,
          sort: sortModel[0].sort,
        },
      }));
    }
  }, []);

  const handleSearch = (value) => {
    if (value?.length > 2 || value === '') {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        total: 0,
        page: 1,
        pageSize: old.pageSize,
        value: value,
      }));
    }
  };

  const handleIsApproveChange = (e) => {
    if (e) {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        total: 0,
        page: 1,
        pageSize: old.pageSize,
        filterOptions: {
          field: 'IsUnApprove',
          value: e,
        },
      }));
    } else {
      setPageState((old) => ({
        ...old,
        isLoading: true,
        total: 0,
        page: 1,
        pageSize: old.pageSize,
        filterOptions: {
          field: 'IsUnApprove',
          value: e,
        },
      }));
    }
  };
  const onConfirmApprove = () => {
    filterLibraryService
      .saveFilter(filterModel)
      .then((response) => {
        if (response.success) {
          toast.success('Record Updated successfully.');
          getFilterLibrary();
          handleClose();
        } else {
          toast.error('Record not Updated. Please try again later.');
        }
      })
      .catch((error) => {});
  };

  const handleDelete = (id) => {
    filterLibraryService
      .deleteFilter(id)
      .then((response) => {
        const { success } = response;
        if (success) {
          toast.success('Record deleted successfully.');
          setIsRemovePopupOpen(false);
          getFilterLibrary();
        }
      })
      .catch((error) => {});
  };

  // Approve validates the same required fields as before, surfaces the same snackbar error, and
  // otherwise opens the confirmation with the identical save model.
  const handleApprove = (row) => {
    if (row.keyWord === null || row.jobRole === null) {
      setSnackbar({
        children: row.keyWord === null ? 'Job Title is required' : 'Job Role is required',
        severity: 'error',
      });
    } else {
      setIsApprovePopupOpen(true);
      const model = {
        Id: row.id,
        KeyWord: row.keyWord,
        JobRole: row.jobRole,
        Department: row.department,
        PhaseYear: row.phaseYear,
        IsApproved: true,
        isApproval: true,
      };
      setFilterModel(model);
    }
  };

  // Toggle server-side sort direction for a column, reusing the existing sort handler.
  const toggleSort = (field) => {
    const sameField = pageState.queryOptions.field === field;
    const sort = sameField && pageState.queryOptions.sort === 'asc' ? 'desc' : 'asc';
    handleSortModelChange([{ field, sort }]);
  };

  const sortHeader = (label, field) => (
    <HeaderSort type='button' onClick={() => toggleSort(field)}>
      {label}
      {pageState.queryOptions.field === field && (
        <SortArrow>{pageState.queryOptions.sort === 'asc' ? '↑' : '↓'}</SortArrow>
      )}
    </HeaderSort>
  );

  const columns = [
    { key: 'keyWord', header: sortHeader('Job Title', 'keyWord'), width: '16%', render: (r) => <TruncatedText value={r.keyWord} /> },
    { key: 'jobRole', header: sortHeader('Job Role', 'jobRole'), render: (r) => <TruncatedText value={r.jobRole} /> },
    { key: 'department', header: sortHeader('Department', 'department'), render: (r) => <TruncatedText value={r.department} /> },
    { key: 'phaseYear', header: sortHeader('Phase Year', 'phaseYear'), render: (r) => <TruncatedText value={r.phaseYear} /> },
    { key: 'school', header: sortHeader('School', 'school'), render: (r) => <TruncatedText value={r.school} /> },
    {
      key: 'isHOD',
      header: 'HOD',
      width: '80px',
      align: 'center',
      render: (r) =>
        r.isHOD ? (
          <HodCell $on>
            <CheckIcon fontSize='small' />
          </HodCell>
        ) : (
          <HodCell>
            <CloseIcon fontSize='small' />
          </HodCell>
        ),
    },
    {
      key: 'isApproved',
      header: 'Approved',
      width: '140px',
      render: (r) => {
        const tone = r.isApproved ? APPROVED_TONE : UNAPPROVED_TONE;
        return (
          <StatusPill $bg={tone.bg} $fg={tone.fg}>
            {r.isApproved ? 'Approved' : 'Not Approved'}
          </StatusPill>
        );
      },
    },
    {
      key: 'action',
      header: 'Action',
      width: '110px',
      align: 'right',
      render: (r) => (
        <ActionCell>
          {/* Edit is offered on every row, not only approved ones. An unapproved row is precisely
              the one that needs editing: the library flags a job title with no job role or
              department, and those have to be filled in before it can be approved — while approving
              refuses to proceed without a job role. Offering only Approve on those rows left the
              two actions deadlocked against each other. */}
          <Tooltip title={r.isApproved ? 'Edit' : 'Edit and approve'}>
            <IconButton
              size='small'
              aria-label='Edit filter'
              onClick={() => {
                setFilterId(JSON.stringify(r.id, null, 4));
                setIsShowEditFilter(true);
              }}
            >
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {!r.isApproved && (
            <Tooltip title='Approve Filter'>
              <IconButton size='small' aria-label='Approve filter' onClick={() => handleApprove(r)}>
                <AddTaskIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title='Delete'>
            <IconButton
              size='small'
              aria-label='Delete filter'
              onClick={() => {
                const id = JSON.stringify(r.id, null, 4);
                setFilterId(id);
                setIsRemovePopupOpen(true);
              }}
            >
              <DeleteIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        </ActionCell>
      ),
    },
  ];

  return (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>
          <Page>
            <SectionHeaderRow title='Filter Library' size='lg'>
              <FilterField>
                <Checkbox
                  size='small'
                  sx={{ p: 0, color: sa.color.teal, '&.Mui-checked': { color: sa.color.teal } }}
                  checked={isUnApproved}
                  name='Unapproved'
                  onChange={(e) => {
                    handleIsApproveChange(e.target.checked);
                    setIsUnApproved(e.target.checked);
                  }}
                />
                Unapproved
              </FilterField>
              <PillButton type='button' onClick={() => setIsShowAddFilter(true)}>
                Add New Filter
              </PillButton>
            </SectionHeaderRow>

            
            <FilterBar>
              <SearchBox>
                <SearchIcon />
                <input
                  id='search'
                  name='search'
                  placeholder='Enter keyword'
                  aria-label='Search filters'
                  onChange={(e) => {
                    handleSearch(e.target.value);
                  }}
                />
              </SearchBox>
            </FilterBar>
            

            <div>
              <DataTable
                columns={columns}
                rows={pageState.data}
                getRowKey={(r) => r.id}
                empty={pageState.isLoading ? 'Loading…' : 'No record'}
              />
              <PaginationWrap>
                <TablePagination
                  component='div'
                  count={pageState.total}
                  page={pageState.page - 1}
                  rowsPerPage={pageState.pageSize}
                  onPageChange={(_event, newPage) =>
                    setPageState((old) => ({
                      ...old,
                      page: newPage + 1,
                    }))
                  }
                  onRowsPerPageChange={(event) =>
                    setPageState((old) => ({
                      ...old,
                      pageSize: parseInt(event.target.value, 10),
                    }))
                  }
                  rowsPerPageOptions={[10, 30, 50, 70, 100]}
                />
              </PaginationWrap>
            </div>
          </Page>
        </MainContentContainer>
      </BodyContainer>
      {isShowAddFilter ? (
        <AddFilter handleClose={handleClose} getFilterLibrary={getFilterLibrary} />
      ) : (
        <div></div>
      )}
      {isShowEditFilter ? (
        <EditFilter
          handleClose={handleClose}
          filterId={filterId}
          getFilterLibrary={getFilterLibrary}
        />
      ) : (
        <div></div>
      )}
      {isRemovePopupOpen ? (
        <ConfirmPopupForm
          isOpen={isRemovePopupOpen}
          onClose={() => {
            setIsRemovePopupOpen(false);
            setFilterId('');
          }}
          onOk={() => {
            handleDelete(filterId);
          }}
          successPopupMessage='Are you sure to remove this item?'
        />
      ) : (
        <div></div>
      )}
      {isApprovePopupOpen ? (
        <ConfirmPopupForm
          isOpen={isApprovePopupOpen}
          onClose={() => {
            setIsApprovePopupOpen(false);
            setFilterModel(null);
          }}
          onOk={() => {
            onConfirmApprove(filterModel);
          }}
          successPopupMessage='Are you sure to approve this record?'
        />
      ) : (
        <div></div>
      )}
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </AppContainer>
  );
};

const mapDispatchToProps = (dispatch) => ({
  loaderActions: bindActionCreators(loaderAction, dispatch),
});
export default connect(null, mapDispatchToProps)(FilterLibrary);
